import React from "react";
import {
  TextField,
  Button,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import LoadingButton from '@mui/lab/LoadingButton';
import { Link, useNavigate } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { authAction } from "../../slices/authSlice";
import { useAppDispatch, useAppSelector } from "../../hooks";
import SendIcon from '@mui/icons-material/Send';

interface FormData {
  email: string;
  password: string;
}

const validationSchema = yup.object().shape({
  email: yup.string().email("Invalid email address").required("Email is required"),
  password: yup.string().min(8, "Password must be at least 8 characters long").required("Password is required"),
});

const Login: React.FC = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const theme = useTheme();
  const { loading } = useAppSelector(state=> state.auth)
  const { control, handleSubmit, formState: { errors } } = useForm<FormData>({
    resolver: yupResolver(validationSchema),
    mode: 'onChange',
  });

  const onSubmit = async (data: FormData) => {    
    const dispatchData = await dispatch(authAction({method: 'post', data: data, actionType: 'login' }))
    try{
      if(dispatchData.meta.requestStatus === 'fulfilled'){
        navigate('/admin/posts')
      }
    }catch(error: any){
      throw new Error(error)
    }
  };

  return (
    <>
      <Typography component="h1" variant="h5" gutterBottom>
        Sign in
      </Typography>
      <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
        <Controller
          name="email"
          control={control}
          defaultValue=""
          render={({ field }) => (
            <TextField
              {...field}
              variant="standard"
              margin="normal"
              fullWidth
              id="email"
              label="Email"
              type="text"
              autoFocus
              error={!!errors.email}
              helperText={errors.email?.message}
            />
          )}
        />
        <Controller
          name="password"
          control={control}
          defaultValue=""
          render={({ field }) => (
            <TextField
              {...field}
              variant="standard"
              margin="normal"
              fullWidth
              label="Password"
              type="password"
              id="password"
              error={!!errors.password}
              helperText={errors.password?.message}
            />
          )}
        />
        {
          loading ?
          <LoadingButton
            fullWidth
            endIcon={<SendIcon />}
            loading={Boolean(loading)}
            loadingPosition="end"
            variant="contained"
            sx={{ mt: 3, mb: 2 }}
            >
            <span>Loading</span>
          </LoadingButton>
          :
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 3, mb: 2 }}
          >
          Sign In
        </Button>
      }
      </form>
      <Link to="/forgot-password" style={{ textDecoration: 'none', userSelect: 'none' }}>
        <Typography sx={{ fontSize: '12px', my: 3, color: theme.palette.primary.main }}>
          Forgot Password
        </Typography>
      </Link>
    </>
  );
};

export default Login;
